import { cn } from "@/lib/utils/helpers";

/**
 * Same horizontal gutters as edit cards (`px-4 sm:px-6`) so tabs line up with content below and use the full card width.
 */
export const entityEditTabBarWrapClassName = "w-full min-w-0 px-4 sm:px-6 pb-0";

export type EntityEditTabAlign = "distribute" | "start";

/**
 * Tab row: single line with horizontal scroll below `lg` (matches application detail tabs).
 * - `distribute` (default): at `lg+`, the row is full width and tabs share space evenly.
 * - `start`: content-sized, left-aligned tabs (Application detail only, while underwrite checklist is open).
 */
export function entityEditTabListClassName(align: EntityEditTabAlign = "distribute") {
    return cn(
        "flex flex-nowrap items-stretch border-b border-slate-200",
        "gap-x-0.5 sm:gap-x-0",
        align === "distribute"
            ? "min-w-full w-max lg:w-full lg:min-w-0 lg:gap-0"
            : "w-max min-w-0 justify-start gap-0",
    );
}

/**
 * Flat tabs with optional nook on active.
 * Large screens (`distribute`): equal flex columns + wrapped labels for even use of width.
 * `start`: content-sized, left-aligned labels with compact horizontal padding.
 */
export function entityEditTabButtonClassName(
    selected: boolean,
    options?: { disabled?: boolean; align?: EntityEditTabAlign },
) {
    const disabled = options?.disabled === true;
    const align = options?.align ?? "distribute";
    return cn(
        "min-w-0 border-b-2 font-medium text-xs sm:text-sm transition-colors",
        "py-3 sm:py-4",
        "flex items-center gap-1.5 leading-snug",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-button/30 focus-visible:ring-inset",
        align === "distribute"
            ? cn(
                  "px-1.5 sm:px-2.5 justify-center text-center whitespace-nowrap lg:whitespace-normal",
                  "shrink-0 grow-0",
                  "lg:min-w-0 lg:flex-1 lg:shrink lg:justify-center",
              )
            : cn(
                  "px-2.5 sm:px-3 justify-start text-left whitespace-nowrap",
                  "shrink-0 grow-0",
              ),
        selected && !disabled
            ? cn(
                  "relative z-[1] -mb-px rounded-t-lg",
                  "bg-button-soft",
                  "border-button text-button",
              )
            : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300",
        disabled && "pointer-events-none cursor-not-allowed opacity-50",
    );
}
